import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  isTrackReference,
  LiveKitRoom,
  RoomAudioRenderer,
  useLocalParticipant,
  useRoomContext,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { CompositorCanvas, useLayoutChannel } from '@liveforge/compositor';
import type { GuestLiveInfo, LiveKitConnection } from '@liveforge/shared';
import {
  CameraIcon,
  CloseIcon,
  EyeIcon,
  MicIcon,
  ScreenShareIcon,
} from '../components/studio/icons';
import { Button } from '../components/ui/button';
import { Field, FormError } from '../components/ui/field';
import { ErrorBoundary } from '../components/ui/error-boundary';
import { LoadingScreen } from '../components/ui/spinner';
import { errorMessage, relayOrigin } from '../lib/api';
import { describeDeviceError } from '../lib/media';
import { getGuestConnection, getGuestLiveInfo } from '../lib/resources';
import '@livekit/components-styles';
import '../styles/guest.css';

interface DeviceChoice {
  videoId: string;
  audioId: string;
}

/**
 * The in-call view for a guest: the program, everyone's audio, and only the
 * controls over their own devices.
 *
 * A guest sees **the show, not the room**. The composition is drawn here from
 * the same compositor Egress will run, over the tracks this browser already
 * receives - so it costs no extra bandwidth and, unlike anything routed back
 * through Egress, is not seconds behind the conversation the guest is having.
 *
 * They still *hear* everyone, including anyone the director has taken off air:
 * a guest has to hear a cue from someone who is not currently on the
 * broadcast. That is also why the programme mixer is not mounted here - it
 * would double every voice against RoomAudioRenderer.
 */
function GuestCall({
  onLeave,
  displayName,
  devices,
  liveId,
}: {
  onLeave: () => void;
  displayName: string;
  devices: DeviceChoice;
  liveId: string;
}) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } =
    useLocalParticipant();

  // The token carries the name recorded on the invite. If the guest edited it on
  // the pre-join screen, push the new one so the host's tile shows what they
  // actually typed.
  useEffect(() => {
    const wanted = displayName.trim();
    if (wanted && wanted !== localParticipant.name) {
      void localParticipant.setName(wanted);
    }
  }, [displayName, localParticipant]);

  const [deviceIssue, setDeviceIssue] = useState<string | null>(null);
  const published = useRef(false);

  // Published after connecting rather than through LiveKitRoom's audio/video
  // props: a device that vanished between the preview and the join would
  // otherwise abort the connection instead of degrading to audio-only.
  useEffect(() => {
    if (published.current) {
      return;
    }
    published.current = true;

    void (async () => {
      const problems: string[] = [];
      try {
        await localParticipant.setMicrophoneEnabled(
          true,
          devices.audioId ? { deviceId: devices.audioId } : undefined,
        );
      } catch (cause) {
        problems.push(describeDeviceError(cause, 'microphone'));
      }
      try {
        await localParticipant.setCameraEnabled(
          true,
          devices.videoId ? { deviceId: devices.videoId } : undefined,
        );
      } catch (cause) {
        problems.push(describeDeviceError(cause, 'camera'));
      }
      setDeviceIssue(problems.length > 0 ? problems.join(' ') : null);
    })();
  }, [devices.audioId, devices.videoId, localParticipant]);

  async function toggle(action: () => Promise<unknown>, kind: 'camera' | 'microphone') {
    try {
      await action();
      setDeviceIssue(null);
    } catch (cause) {
      setDeviceIssue(describeDeviceError(cause, kind));
    }
  }
  const room = useRoomContext();

  // The memo is no longer load-bearing - useLayoutChannel keys on the values
  // now - but it costs nothing and keeps the object stable for the reader.
  const channel = useMemo(() => ({ liveId, apiUrl: relayOrigin() }), [liveId]);
  const { layers, connected } = useLayoutChannel(channel);

  const [showSelf, setShowSelf] = useState(false);

  // Only the guest's own camera. Everyone else reaches them through the
  // program; this exists so they can tell how they are framed.
  // `useTracks` types its result as possibly-placeholder whatever the options
  // say, so the guard narrows it rather than asserting.
  const selfTrack = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }], {
    onlySubscribed: false,
  })
    .filter(isTrackReference)
    .find((track) => track.participant.isLocal);

  const waiting = layers === null ? 'Connecting to the program…' : null;
  const empty = layers !== null && layers.length === 0 ? 'Nothing is on air yet.' : null;

  return (
    <div className="guest-call">
      <div className="guest-call__topbar">
        <span className="guest-brand">
          <span className="guest-brand__mark" aria-hidden="true" />
          LiveForge
        </span>
        <span className="guest-live" data-short="Live">
          <span className="guest-live__dot" aria-hidden="true" />
          <span className="guest-live__text">You are live in the studio</span>
        </span>
      </div>

      <div className="guest-call__stage">
        <div className="guest-call__program">
          {/*
            Composed well below the broadcast size and capped in frame rate. A
            guest's screen is small, and full HD at display refresh would cost
            what Egress pays - enough to make a phone throttle, which degrades
            the call itself and not only the picture.
          */}
          <CompositorCanvas
            room={room}
            layers={layers ?? []}
            className="guest-call__canvas"
            renderWidth={960}
            renderHeight={540}
            maxFps={30}
          />

          {(waiting ?? empty) ? (
            <p className="guest-call__overlay muted">{waiting ?? empty}</p>
          ) : null}

          {showSelf && selfTrack ? (
            <div className="guest-call__self">
              <VideoTrack trackRef={selfTrack} />
            </div>
          ) : null}
        </div>
      </div>

      {deviceIssue ? (
        <p className="alert alert--warning" role="status">
          {deviceIssue} You are still in the call.
        </p>
      ) : null}

      <p className="guest-call__hint muted">
        This is the program - what the audience sees. You still hear everyone in the room, including
        anyone the director has taken off air.
        {connected ? null : ' The program is not updating right now.'}
      </p>

      {/* Plays every remote audio track. Without it the guest is deaf. */}
      <RoomAudioRenderer />

      <div className="control-bar">
        <button
          type="button"
          className={`control-button ${isMicrophoneEnabled ? 'control-button--active' : 'control-button--off'}`}
          data-short="Mic"
          onClick={() =>
            void toggle(
              () => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled),
              'microphone',
            )
          }
        >
          <MicIcon on={isMicrophoneEnabled} />
          <span className="control-button__text">
            {isMicrophoneEnabled ? 'Mute mic' : 'Unmute mic'}
          </span>
        </button>
        <button
          type="button"
          className={`control-button ${isCameraEnabled ? 'control-button--active' : 'control-button--off'}`}
          data-short="Cam"
          onClick={() =>
            void toggle(() => localParticipant.setCameraEnabled(!isCameraEnabled), 'camera')
          }
        >
          <CameraIcon on={isCameraEnabled} />
          <span className="control-button__text">
            {isCameraEnabled ? 'Stop camera' : 'Start camera'}
          </span>
        </button>
        <button
          type="button"
          className={`control-button ${isScreenShareEnabled ? 'control-button--active' : ''}`}
          data-short="Share"
          onClick={() =>
            void toggle(
              () => localParticipant.setScreenShareEnabled(!isScreenShareEnabled),
              'camera',
            )
          }
        >
          <ScreenShareIcon />
          <span className="control-button__text">
            {isScreenShareEnabled ? 'Stop sharing' : 'Share screen'}
          </span>
        </button>
        <button
          type="button"
          className={`control-button ${showSelf ? 'control-button--active' : ''}`}
          data-short="Self"
          onClick={() => setShowSelf((shown) => !shown)}
        >
          <EyeIcon on={showSelf} />
          <span className="control-button__text">{showSelf ? 'Hide self view' : 'Self view'}</span>
        </button>
        <button
          type="button"
          className="control-button control-button--leave"
          data-short="Leave"
          onClick={onLeave}
        >
          <CloseIcon />
          <span className="control-button__text">Leave</span>
        </button>
      </div>
    </div>
  );
}

export function GuestJoinPage() {
  const { token } = useParams<{ token: string }>();

  const [info, setInfo] = useState<GuestLiveInfo | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');

  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [choice, setChoice] = useState<DeviceChoice>({ videoId: '', audioId: '' });
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const [connection, setConnection] = useState<LiveKitConnection | null>(null);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Mirrors streamRef for rendering: a ref never triggers a re-render, so the
  // overlay would otherwise stay up after permission was granted.
  const [previewActive, setPreviewActive] = useState(false);

  /** Releases the preview camera and microphone. A leaked track keeps the light on. */
  const stopPreview = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setPreviewActive(false);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setInfoError('This invite link is missing its token.');
      return;
    }
    const controller = new AbortController();
    getGuestLiveInfo(token, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setInfo(data);
        setDisplayName(data.guestName);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setInfoError(errorMessage(cause));
      });
    return () => controller.abort();
  }, [token]);

  // Device labels stay empty until permission is granted, so getUserMedia has to
  // run before enumerateDevices - not after.
  const startPreview = useCallback(
    async (next?: Partial<DeviceChoice>) => {
      const wanted: DeviceChoice = {
        videoId: next?.videoId ?? choice.videoId,
        audioId: next?.audioId ?? choice.audioId,
      };
      setPermissionError(null);
      stopPreview();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: wanted.videoId ? { deviceId: { exact: wanted.videoId } } : true,
          audio: wanted.audioId ? { deviceId: { exact: wanted.audioId } } : true,
        });
        streamRef.current = stream;
        setPreviewActive(true);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        const devices = await navigator.mediaDevices.enumerateDevices();
        setVideoDevices(devices.filter((device) => device.kind === 'videoinput'));
        setAudioDevices(devices.filter((device) => device.kind === 'audioinput'));

        const activeVideo = stream.getVideoTracks()[0]?.getSettings().deviceId ?? '';
        const activeAudio = stream.getAudioTracks()[0]?.getSettings().deviceId ?? '';
        setChoice({
          videoId: wanted.videoId || activeVideo,
          audioId: wanted.audioId || activeAudio,
        });
      } catch (cause) {
        setPermissionError(
          cause instanceof DOMException && cause.name === 'NotAllowedError'
            ? 'The browser blocked access to your camera and microphone. Allow them and try again.'
            : errorMessage(cause),
        );
      }
    },
    [choice.audioId, choice.videoId, stopPreview],
  );

  // Release the devices when the component goes away for any reason.
  useEffect(() => stopPreview, [stopPreview]);

  if (infoError) {
    return (
      <div className="guest-error-page">
        <div className="guest-error-page__box">
          <h1>Invite not available</h1>
          <p className="muted">This invite link is invalid or has expired.</p>
          <p className="muted mono">{infoError}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return <LoadingScreen label="Checking your invite..." />;
  }

  async function handleJoin() {
    if (!token) {
      return;
    }
    setJoinError(null);
    setJoining(true);
    try {
      const data = await getGuestConnection(token);
      // LiveKitRoom acquires its own tracks; holding the preview open would keep
      // a second handle on the same camera.
      stopPreview();
      setConnection(data);
    } catch (cause) {
      setJoinError(errorMessage(cause));
    } finally {
      setJoining(false);
    }
  }

  if (connection) {
    return (
      <LiveKitRoom
        className="guest-page"
        data-lk-theme="default"
        token={connection.token}
        serverUrl={connection.url}
        connect
        // See PublishLocalMedia's note in the studio: devices are acquired
        // after connecting so a missing one cannot abort the join.
        audio={false}
        video={false}
        onError={(cause) => setJoinError(errorMessage(cause))}
        onDisconnected={() => setConnection(null)}
      >
        {/*
          A guest is someone else's user on someone else's phone: a black page
          tells them nothing and tells the director less. Inside the room, so
          the connection is torn down with it rather than left publishing.
        */}
        <ErrorBoundary label="guest page">
          <GuestCall
            onLeave={() => setConnection(null)}
            displayName={displayName}
            devices={choice}
            liveId={info.liveId}
          />
        </ErrorBoundary>
      </LiveKitRoom>
    );
  }

  return (
    <div className="guest-page">
      <div className="guest-card">
        <span className="guest-brand">
          <span className="guest-brand__mark" aria-hidden="true" />
          LiveForge
        </span>
        <div className="guest-card__header">
          <h1>{info.liveTitle}</h1>
          <p className="guest-card__show muted">{info.showName}</p>
        </div>

        <div className="guest-preview">
          <video className="guest-preview__video" ref={videoRef} autoPlay playsInline muted />
          {!previewActive ? (
            <div className="guest-preview__overlay">
              <Button variant="secondary" onClick={() => void startPreview()}>
                Enable camera and microphone
              </Button>
            </div>
          ) : null}
        </div>

        <FormError>{permissionError ?? joinError}</FormError>

        <div className="guest-form">
          <Field label="Your name">
            {(id) => (
              <input
                id={id}
                className="input"
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            )}
          </Field>

          <Field label="Camera">
            {(id) => (
              <select
                id={id}
                className="select"
                value={choice.videoId}
                disabled={videoDevices.length === 0}
                onChange={(event) => void startPreview({ videoId: event.target.value })}
              >
                {videoDevices.length === 0 ? <option value="">Enable devices first</option> : null}
                {videoDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || 'Camera'}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Microphone">
            {(id) => (
              <select
                id={id}
                className="select"
                value={choice.audioId}
                disabled={audioDevices.length === 0}
                onChange={(event) => void startPreview({ audioId: event.target.value })}
              >
                {audioDevices.length === 0 ? <option value="">Enable devices first</option> : null}
                {audioDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || 'Microphone'}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Button variant="primary" block loading={joining} onClick={() => void handleJoin()}>
            Join
          </Button>
        </div>
      </div>
    </div>
  );
}
